import { Check } from "lucide-react";
import * as React from "react";
import { getLocale, type Locale, updateCurrentLocale } from "../i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Cookie name for locale preference
export const LOCALE_COOKIE_NAME = "parrot-locale";

const LANGUAGES: { code: Locale; label: string }[] = [
  { code: "en", label: "English" },
  { code: "pl", label: "Polski" },
];

// Set a cookie with the locale preference (expires in 1 year)
function setLocaleCookie(locale: Locale) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${LOCALE_COOKIE_NAME}=${locale};path=/;expires=${expires.toUTCString()};SameSite=Lax`;
}

export function LanguagePicker() {
  const [locale, setLocale] = React.useState<Locale>("en");

  // Initialize from localStorage on mount
  React.useEffect(() => {
    setLocale(getLocale());
  }, []);

  const handleLocaleChange = (newLocale: Locale) => {
    if (newLocale === locale) return;
    setLocale(newLocale);
    updateCurrentLocale(newLocale);
    setLocaleCookie(newLocale);
    // Reload to apply the new locale across the app
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-8 items-center justify-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label="Select language"
        >
          {locale.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[120px]">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLocaleChange(lang.code)}
            className="cursor-pointer justify-between"
          >
            {lang.label}
            {locale === lang.code && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
